import { useEffect, useRef, useState } from 'react'
import { useDebouncedValue } from '@loop3/ui'

const DEBOUNCE_MS = 200

/**
 * Local input state that writes to the URL after a pause, and follows the URL
 * when it changes from elsewhere (e.g. a card linking to `/explorer?q=`).
 */
export function useDebouncedSearchParam(
  urlValue: string,
  write: (value: string) => void
) {
  const [value, setValue] = useState(urlValue)
  const [seenUrl, setSeenUrl] = useState(urlValue)
  const pushed = useRef(urlValue)
  const writeRef = useRef(write)
  writeRef.current = write
  const settled = useDebouncedValue(value, DEBOUNCE_MS)

  if (urlValue !== seenUrl) {
    setSeenUrl(urlValue)
    if (urlValue !== pushed.current) {
      pushed.current = urlValue
      setValue(urlValue)
    }
  }

  useEffect(() => {
    // Only a value the user has stopped typing, and only if the URL doesn't already hold it.
    if (settled !== value || settled === urlValue) return
    pushed.current = settled
    writeRef.current(settled)
  }, [settled, value, urlValue])

  return { value, set: setValue }
}
